/**
 * API URL 工具函数
 * 统一管理 API 地址，支持环境变量配置
 */

const getApiBaseUrl = () => {
  // Vite 环境变量
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // React 环境变量
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // 生产环境使用相对路径
  if (import.meta.env.PROD || process.env.NODE_ENV === 'production') {
    return '';
  }
  // 开发环境默认
  return 'http://localhost:18080';
};

// 图片域名替换配置
const getImageBaseUrl = () => {
  if (import.meta.env.VITE_IMAGE_BASE_URL) {
    return import.meta.env.VITE_IMAGE_BASE_URL;
  }
  // 如果配置了 VITE_API_BASE_URL，使用它作为图片基础URL
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  return '';
};

// 获取前端基础地址（用于静态资源如默认背景图）
const getFrontendBaseUrl = () => {
  if (import.meta.env.VITE_FRONTEND_BASE_URL) {
    return import.meta.env.VITE_FRONTEND_BASE_URL;
  }
  // 开发环境默认
  if (import.meta.env.DEV) {
    return 'http://localhost:3000';
  }
  return '';
};

// 旧域名到新域名的映射（用于替换数据库中的旧URL）
const IMAGE_URL_REPLACEMENTS = [
  // 替换 Docker 内部服务名为相对路径
  { old: 'http://hk-api:8080/uploads/', new: '/uploads/' },
  { old: 'https://hk-api:8080/uploads/', new: '/uploads/' },
  { old: 'http://hk-api:18080/uploads/', new: '/uploads/' },
  { old: 'https://hk-api:18080/uploads/', new: '/uploads/' },
];

/**
 * 获取完整的 API URL
 * @param {string} path - API 路径，如 '/api/user/info'
 * @returns {string} 完整的 URL
 */
export const getApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  const apiPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${apiPath}`;
};

/**
 * 获取 API 基础 URL（不带 /api 后缀，用于 fetch 直接调用）
 * @returns {string} 基础 URL
 */
export const getBaseUrl = () => {
  return getApiBaseUrl();
};

/**
 * 简写函数
 */
export const apiUrl = getApiUrl;

// 导出 base URL 供其他用途（带 /api 后缀）
export const API_BASE_URL = getApiBaseUrl();

// 导出不带 /api 后缀的基础 URL
export const BASE_URL = getApiBaseUrl();

/**
 * 处理图片 URL，根据路径类型选择合适的地址
 * - /uploads/ 开头的用后端地址（18080）
 * - bg.png 等默认背景图用前端地址（3000）
 * @param {string} imageUrl - 原始图片 URL
 * @returns {string} 处理后的图片 URL
 */
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  // 如果已经是完整 URL，直接返回
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    // 应用域名替换规则
    let resultUrl = imageUrl;
    for (const replacement of IMAGE_URL_REPLACEMENTS) {
      if (resultUrl.includes(replacement.old)) {
        resultUrl = resultUrl.replace(replacement.old, replacement.new);
      }
    }
    return resultUrl;
  }
  
  // /uploads/ 开头的用后端地址（API 地址，默认为 http://localhost:18080）
  if (imageUrl.startsWith('/uploads/')) {
    const baseUrl = getApiBaseUrl(); // 使用 API 地址
    return `${baseUrl}${imageUrl}`;
  }
  
  // 其他相对路径（默认背景图如 /bg.png）用前端地址
  if (imageUrl.startsWith('/')) {
    const frontendUrl = getFrontendBaseUrl();
    return frontendUrl ? `${frontendUrl}${imageUrl}` : imageUrl;
  }
  
  // 纯文件名（如 1777766350812526300.png），用后端地址 + /uploads/
  const apiUrl = getApiBaseUrl();
  return `${apiUrl}/uploads/${imageUrl}`;
};

export default getApiUrl;
